import React from "react";
import { createPortal } from "react-dom";
import { useEditorState } from "../../../../editor/state";
import {
  areSameColor,
  normalizeHexColor,
  normalizeStandardColorPalette,
} from "../../lib/colorPalette";

type SelectOptions = {
  close?: boolean;
};

const CLOSE_COLOR_POPOVERS_EVENT = "qe-close-standard-color-popovers";

interface PalettePopoverProps {
  anchorRect: DOMRect;
  value: string;
  allowTransparent?: boolean;
  onSelect: (color: string, options?: SelectOptions) => void;
  onClose: () => void;
}

interface StandardColorPickerProps {
  value: string;
  onChange: (value: string) => void;
  allowTransparent?: boolean;
  title?: string;
}

function getPopoverPosition(anchorRect: DOMRect) {
  const width = 252;
  const height = 160;
  const margin = 10;
  const left = Math.min(
    Math.max(margin, anchorRect.left),
    Math.max(margin, window.innerWidth - width - margin)
  );
  const below = anchorRect.bottom + 8;
  const top =
    below + height <= window.innerHeight - margin
      ? below
      : Math.max(margin, anchorRect.top - height - 8);
  return { left, top, width };
}

function isTransparent(value: string) {
  return value.trim().toLowerCase() === "transparent";
}

function getSwatchStyle(value: string): React.CSSProperties {
  if (isTransparent(value)) {
    return {
      backgroundColor: "#ffffff",
      backgroundImage:
        "linear-gradient(45deg, #cbd5e1 25%, transparent 25%), linear-gradient(-45deg, #cbd5e1 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #cbd5e1 75%), linear-gradient(-45deg, transparent 75%, #cbd5e1 75%)",
      backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
      backgroundSize: "16px 16px",
    };
  }

  return {
    backgroundColor: normalizeHexColor(value) ?? "#ffffff",
  };
}

function setNativeColorInputValue(input: HTMLInputElement, color: string) {
  const valueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    "value"
  )?.set;

  if (valueSetter) {
    valueSetter.call(input, color);
  } else {
    input.value = color;
  }

  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
}

const PalettePopover: React.FC<PalettePopoverProps> = ({
  anchorRect,
  value,
  allowTransparent = true,
  onSelect,
  onClose,
}) => {
  const { canvas, dispatch } = useEditorState();
  const palette = normalizeStandardColorPalette(canvas.colorPalette);
  const [editing, setEditing] = React.useState(false);
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
  const [draftColor, setDraftColor] = React.useState("#000000");
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const position = getPopoverPosition(anchorRect);

  React.useEffect(() => {
    if (!editing) {
      setEditingIndex(null);
      return;
    }

    if (editingIndex === null) {
      const selectedIndex = palette.findIndex((color) =>
        areSameColor(value, color)
      );
      const nextIndex = selectedIndex >= 0 ? selectedIndex : 0;
      setEditingIndex(nextIndex);
      setDraftColor(palette[nextIndex]);
    }
  }, [editing, editingIndex, palette, value]);

  React.useEffect(() => {
    const closeOnOutsideClick = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && popoverRef.current?.contains(target)) {
        return;
      }
      onClose();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose]);

  const updatePaletteColor = (index: number, nextColor: string) => {
    const normalized = normalizeHexColor(nextColor);
    setDraftColor(nextColor);
    if (!normalized) return;

    const oldColor = palette[index];
    dispatch({
      type: "update-color-palette-slot",
      index,
      color: normalized,
    });

    if (areSameColor(value, oldColor)) {
      onSelect(normalized, { close: false });
    }
  };

  return createPortal(
    <div
      ref={popoverRef}
      className="qe-standard-color-popover"
      style={{ left: position.left, top: position.top, width: position.width }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div className="qe-standard-color-popover__toolbar">
        {allowTransparent ? (
          <button
            type="button"
            className={`qe-standard-color-popover__transparent ${
              isTransparent(value)
                ? "qe-standard-color-popover__swatch--selected"
                : ""
            }`}
            style={getSwatchStyle("transparent")}
            aria-label="Şeffaf"
            title="Şeffaf"
            onClick={() => onSelect("transparent")}
          />
        ) : (
          <span />
        )}
        <button
          type="button"
          className={`qe-standard-color-popover__icon ${
            editing ? "qe-standard-color-popover__icon--active" : ""
          }`}
          aria-label="Paleti düzenle"
          title="Paleti düzenle"
          onClick={() => setEditing((current) => !current)}
        >
          {editing ? "✓" : "✎"}
        </button>
        <button
          type="button"
          className="qe-standard-color-popover__icon"
          aria-label="Kapat"
          title="Kapat"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <div className="qe-standard-color-popover__grid">
        {palette.map((color, index) => {
          const selected = !isTransparent(value) && areSameColor(value, color);
          return (
            <div
              key={`${index}-${color}`}
              className="qe-standard-color-popover__swatch-wrap"
            >
              <button
                type="button"
                className={`qe-standard-color-popover__swatch ${
                  selected || editingIndex === index
                    ? "qe-standard-color-popover__swatch--selected"
                    : ""
                }`}
                style={getSwatchStyle(color)}
                aria-label={`Palet rengi ${index + 1}`}
                title={color}
                onClick={() => {
                  if (editing) {
                    setEditingIndex(index);
                    setDraftColor(color);
                    return;
                  }
                  onSelect(color);
                }}
              />
            </div>
          );
        })}
      </div>
      {editing && editingIndex !== null ? (
        <input
          className="qe-standard-color-popover__hex"
          value={draftColor}
          spellCheck={false}
          aria-label="Palet rengi hex"
          onChange={(event) =>
            updatePaletteColor(editingIndex, event.target.value)
          }
        />
      ) : null}
    </div>,
    document.body
  );
};

export const StandardColorPicker: React.FC<StandardColorPickerProps> = ({
  value,
  onChange,
  allowTransparent = true,
  title,
}) => {
  const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);

  const close = React.useCallback(() => setAnchorRect(null), []);

  React.useEffect(() => {
    window.addEventListener(CLOSE_COLOR_POPOVERS_EVENT, close);
    return () => {
      window.removeEventListener(CLOSE_COLOR_POPOVERS_EVENT, close);
    };
  }, [close]);

  const selectColor = React.useCallback(
    (color: string, options?: SelectOptions) => {
      onChange(color);
      if (options?.close !== false) {
        close();
      }
    },
    [close, onChange]
  );

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="qe-standard-color-picker"
        style={getSwatchStyle(value)}
        aria-label={title ?? "Renk seç"}
        title={title ?? value}
        onClick={() => {
          const rect = buttonRef.current?.getBoundingClientRect();
          if (rect) {
            window.dispatchEvent(new Event(CLOSE_COLOR_POPOVERS_EVENT));
            setAnchorRect(rect);
          }
        }}
      />
      {anchorRect ? (
        <PalettePopover
          anchorRect={anchorRect}
          value={value}
          allowTransparent={allowTransparent}
          onSelect={selectColor}
          onClose={close}
        />
      ) : null}
    </>
  );
};

/**
 * Öğe ayarlarındaki HER native `<input type="color">` tıklamasını yakalayıp
 * standart palet popover'ını açar. Böylece renderColorControl dışındaki
 * doğrudan renk inputları da aynı kartelayı kullanır. Uygulamada bir kez
 * mount edilir (EditorLayout).
 */
export const StandardColorInputBridge: React.FC = () => {
  const [targetInput, setTargetInput] = React.useState<HTMLInputElement | null>(
    null
  );
  const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null);
  const [value, setValue] = React.useState("#000000");

  React.useEffect(() => {
    const openForNativeInput = (event: Event) => {
      const target = event.target as HTMLElement | null;
      const input = target?.closest?.("input[type='color']") as
        | HTMLInputElement
        | null;

      if (
        !input ||
        input.dataset.qeNativeColorEdit === "true" ||
        input.closest(".qe-standard-color-popover")
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      window.dispatchEvent(new Event(CLOSE_COLOR_POPOVERS_EVENT));
      setTargetInput(input);
      setValue(input.value || "#000000");
      setAnchorRect(input.getBoundingClientRect());
    };

    document.addEventListener("pointerdown", openForNativeInput, true);
    document.addEventListener("mousedown", openForNativeInput, true);
    document.addEventListener("click", openForNativeInput, true);
    return () => {
      document.removeEventListener("pointerdown", openForNativeInput, true);
      document.removeEventListener("mousedown", openForNativeInput, true);
      document.removeEventListener("click", openForNativeInput, true);
    };
  }, []);

  const close = React.useCallback(() => {
    setAnchorRect(null);
    setTargetInput(null);
  }, []);

  React.useEffect(() => {
    window.addEventListener(CLOSE_COLOR_POPOVERS_EVENT, close);
    return () => {
      window.removeEventListener(CLOSE_COLOR_POPOVERS_EVENT, close);
    };
  }, [close]);

  const selectColor = React.useCallback(
    (color: string, options?: SelectOptions) => {
      const normalized = normalizeHexColor(color);
      if (!targetInput || !normalized) return;

      setNativeColorInputValue(targetInput, normalized);
      setValue(normalized);
      if (options?.close !== false) {
        close();
      }
    },
    [close, targetInput]
  );

  return anchorRect ? (
    <PalettePopover
      anchorRect={anchorRect}
      value={value}
      allowTransparent={false}
      onSelect={selectColor}
      onClose={close}
    />
  ) : null;
};
